// TikTok Pixel - Tracking utility
// Pixel ID: D7DQHO3C77U75VFHCA4G

const TIKTOK_PIXEL_ID = 'D7DQHO3C77U75VFHCA4G';

declare global {
  interface Window {
    TiktokAnalyticsObject: string;
    ttq: TikTokPixel;
  }
}

interface TikTokPixel {
  push: (args: unknown[]) => void;
  methods: string[];
  setAndDefer: (t: TikTokPixel, e: string) => void;
  instance: (t: string) => TikTokPixel;
  load: (e: string, n?: { partner?: string }) => void;
  _i: Record<string, unknown>;
  _t: Record<string, number>;
  _o: Record<string, unknown>;
  // Methods
  page: () => void;
  track: (event: string, params?: Record<string, unknown>) => void;
  identify: (...args: unknown[]) => void;
  instances: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  on: (...args: unknown[]) => void;
  off: (...args: unknown[]) => void;
  once: (...args: unknown[]) => void;
  ready: (...args: unknown[]) => void;
  alias: (...args: unknown[]) => void;
  group: (...args: unknown[]) => void;
  enableCookie: (...args: unknown[]) => void;
  disableCookie: (...args: unknown[]) => void;
  holdConsent: (...args: unknown[]) => void;
  revokeConsent: (...args: unknown[]) => void;
  grantConsent: (...args: unknown[]) => void;
}

/** Initialize TikTok Pixel - already loaded in index.html <head>, this is a no-op guard */
export function initTiktokPixel() {
  // Script is loaded statically in index.html <head> as recommended by TikTok
  // This function exists for compatibility with the App.tsx initialization flow
}

/** Track page view - call on route change */
export function trackTiktokPageView() {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.page();
  }
}

/** Track ViewContent - product page views */
export function trackTiktokViewContent(params: {
  content_id?: string;
  content_type?: string;
  content_name?: string;
  value?: number;
  currency?: string;
}) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track('ViewContent', params);
  }
}

/** Track AddToCart */
export function trackTiktokAddToCart(params: {
  content_id?: string;
  content_type?: string;
  content_name?: string;
  quantity?: number;
  value?: number;
  currency?: string;
}) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track('AddToCart', params);
  }
}

/** Track InitiateCheckout */
export function trackTiktokInitiateCheckout(params: {
  value?: number;
  currency?: string;
  content_ids?: string[];
  content_type?: string;
  num_items?: number;
}) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track('InitiateCheckout', params);
  }
}

/** Track Purchase with optional event_id for deduplication */
export function trackTiktokPurchase(params: {
  value: number;
  currency: string;
  content_ids?: string[];
  content_type?: string;
  num_items?: number;
  order_id?: string;
}, eventId?: string) {
  if (typeof window !== 'undefined' && window.ttq) {
    const purchaseParams: Record<string, unknown> = { ...params };
    if (eventId) {
      purchaseParams.event_id = eventId;
    }
    window.ttq.track('Purchase', purchaseParams);
  }
}

/** Track custom events */
export function trackTiktokCustomEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track(event, params);
  }
}

/** Track CompleteRegistration (for auth) */
export function trackTiktokCompleteRegistration(params?: {
  value?: number;
  currency?: string;
}) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track('CompleteRegistration', params);
  }
}

/** Track Search */
export function trackTiktokSearch(params: {
  search_string: string;
}) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track('Search', params);
  }
}
